import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { 
  Star,
  LogOut,
  Flame,
  Settings,
  Sparkles,
  Users,
  GraduationCap,
  Heart,
  RotateCcw,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";
import { NetworkGraph } from "@/components/NetworkGraph";
import FirstLoginPasswordGate from "@/components/FirstLoginPasswordGate";

const Dashboard = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ points: 0, streak: 0 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [reinforcements, setReinforcements] = useState([]);
  const [enabledSubjects, setEnabledSubjects] = useState(null);
  const [enabledProducts, setEnabledProducts] = useState(null);
  const [firstLoginGateOpen, setFirstLoginGateOpen] = useState(false);

  useEffect(() => {
    // Check if user needs onboarding (students only)
    if (user && !user.onboarding_completed && user.role === "student") {
      navigate("/onboarding");
      return;
    }

    // Role-based redirects
    if (user && user.role === "parent") {
      navigate("/parent", { replace: true });
      return;
    }
    if (user && user.role === "institutional_admin") {
      navigate("/institution-admin", { replace: true });
      return;
    }
    if (user && user.role === "super_admin") {
      navigate("/super-admin", { replace: true });
      return;
    }

    // Teachers land on the student view to preview tutors/subjects
    // If teacher has no grade set yet, redirect to select-grade first
    if (user && user.role === "teacher" && !user.grade) {
      navigate("/select-grade", { replace: true });
      return;
    }

    // Fase 9 auto-redirect · RESTAURADO para B2C ÚNICAMENTE (comportamiento pre-M3.1 intacto).
    // Discriminación por `access_type` autoritativo del backend (contrato existente desde
    // Fase 8 B2B M1). No hay inferencia frágil: si el backend clasifica al usuario como
    // "b2c" via school.school_type=="b2c", auto-redirect a /avanzo (mismo flujo original).
    // Si el backend clasifica como "b2b", NO redirect — el student aterriza en el dashboard
    // institucional y accede a Buddy Avanza vía el nodo explícito + FirstLoginPasswordGate.
    if (user && user.role === "student") {
      axios.get(`${API}/avanzo-b2c/access`, { withCredentials: true })
        .then((r) => {
          if (r.data?.allowed && r.data?.access_type === "b2c") {
            navigate("/avanzo", { replace: true });
          }
          // access_type === "b2b" → deliberately NO redirect; B2B UI flow takes over.
        })
        .catch(() => { /* no avanza access → seguir en dashboard institucional */ });
    }

    const fetchStats = async () => {
      try {
        await axios.post(`${API}/stats/activity`, {}, { withCredentials: true });
        const response = await axios.get(`${API}/stats/me`, { withCredentials: true });
        setStats({ 
          points: response.data.points || 0,
          streak: response.data.streak || 0
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    const fetchReinforcements = async () => {
      try {
        const res = await axios.get(`${API}/student/reinforcements`, { withCredentials: true });
        setReinforcements(res.data || []);
      } catch (error) {
        console.error("Error fetching reinforcements:", error);
      }
    };

    fetchStats();
    fetchReinforcements();

    // Subjects enabled for this user/school (drives which subject nodes appear)
    const fetchSubjects = async () => {
      try {
        const res = await axios.get(`${API}/me/subjects-enabled`, { withCredentials: true });
        setEnabledSubjects(res.data?.subjects_enabled || null);
      } catch (e) { /* keep default 4 subjects */ }
    };
    fetchSubjects();

    // M3.1 · Products the student's school has enabled (Buddy Avanza etc.)
    // Independent of subjects_enabled. Zero PII in the payload.
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/me/products-enabled`, { withCredentials: true });
        setEnabledProducts(res.data?.products_enabled || {});
      } catch (e) {
        setEnabledProducts({}); // fail-closed
      }
    };
    fetchProducts();
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/login");
    } catch (error) {
      toast.error("Error al cerrar sesión");
    }
  };

  // M3.1 · Buddy Avanza gated entry. Probes /avanzo-b2c/access before
  // navigating; if backend responds 403 password_change_required, opens
  // the first-login modal instead of failing silently.
  const openBuddyAvanza = async () => {
    try {
      const r = await axios.get(`${API}/avanzo-b2c/access`, { withCredentials: true });
      if (r?.data?.allowed) {
        navigate("/avanzo");
        return;
      }
      // Backend explicitly denied but not with 403 — fall through gracefully.
      toast.error("No es posible acceder a Buddy Avanza en este momento.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "password_change_required") {
        setFirstLoginGateOpen(true);
        return;
      }
      if (typeof detail === "string" && detail.startsWith("consent_")) {
        toast.error(
          detail === "consent_pending"
            ? "Falta registrar tu consentimiento. Contacta a tu institución."
            : "Tu acceso a Buddy Avanza fue revocado. Contacta a tu institución."
        );
        return;
      }
      toast.error(
        typeof detail === "string" ? detail : "No es posible acceder a Buddy Avanza."
      );
    }
  };

  const handleNodeOpen = (nodeId) => {
    if (nodeId === "buddy-avanza") {
      openBuddyAvanza();
      return;
    }
    if (nodeId === "reto-avanzo") {
      navigate(`/tutor/reto-avanzo`);
      return;
    }
    navigate(`/rutas/${nodeId}`);
  };

  const handleNodeTutor = (nodeId) => {
    if (nodeId === "buddy-avanza") {
      openBuddyAvanza();
      return;
    }
    if (nodeId === "reto-avanzo") {
      navigate(`/tutor/reto-avanzo`);
      return;
    }
    navigate(`/rutas/${nodeId}`);
  };

  if (!user) return null;

  const displayName = user.profile?.first_name || user.name?.split(" ")[0] || "Estudiante";

  const subjectLabels = {
    "comunicacion-lenguaje": { name: "Comunicacion y Lenguaje", color: "#ef4444" },
    "ciencia-tecnologia": { name: "Ciencia y Tecnologia", color: "#14b8a6" },
    "matematica-datos": { name: "Matematica", color: "#8b5cf6" },
    "ciudadania-valores": { name: "Ciudadania y Valores", color: "#f59e0b" },
  };

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="dashboard">
      {/* Header */}
      <header className="qhub-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <QHubBuddyLogo size="default" />

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Grade badge */}
              {user.grade && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#419cca]/10 border border-[#419cca]/20">
                <GraduationCap className="w-4 h-4 text-[#419cca]" />
                <span className="text-sm font-medium text-[#419cca]">Grado {user.grade}</span>
              </div>
              )}

              {/* Streak */}
              <div className="streak-badge hidden sm:flex">
                <Flame className="w-4 h-4" />
                <span>{stats.streak} días</span>
              </div>

              {/* Points */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200">
                <Star className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-purple-600">{stats.points} pts</span>
              </div>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 p-1 hover:bg-gray-100" data-testid="user-menu-btn">
                    <Avatar className="h-9 w-9 border-2 border-[#419cca]/30">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-gradient-to-br from-[#419cca] to-[#8b5cf6] text-white">
                        {displayName?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200">
                  <div className="px-3 py-2">
                    <p className="font-medium text-gray-900">{user.profile ? `${user.profile.first_name} ${user.profile.last_name}` : user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-gray-700 focus:bg-gray-50"
                  >
                    <Flame className="w-4 h-4 mr-2 text-orange-500" />
                    Racha: {stats.streak} días
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-gray-700 focus:bg-gray-50"
                  >
                    <Star className="w-4 h-4 mr-2 text-purple-500" />
                    Puntos: {stats.points}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate("/onboarding")} 
                    className="text-gray-700 focus:bg-gray-50"
                    data-testid="menu-edit-profile"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Editar Perfil
                  </DropdownMenuItem>
                  {(user.role === "teacher" || user.role === "admin") && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/teacher")} 
                      className="text-gray-700 focus:bg-gray-50"
                      data-testid="menu-teacher"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Panel Docente
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem 
                    onClick={() => navigate("/parent")} 
                    className="text-gray-700 focus:bg-gray-50"
                    data-testid="menu-parent"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Panel de Padres
                  </DropdownMenuItem>

                  {user.role === "institutional_admin" && (
                    <DropdownMenuItem
                      onClick={() => navigate("/institution-admin")}
                      className="text-gray-700 focus:bg-gray-50"
                      data-testid="menu-institution-admin"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Panel Institución
                    </DropdownMenuItem>
                  )}

                  {user.role === "super_admin" && (
                    <DropdownMenuItem
                      onClick={() => navigate("/super-admin")}
                      className="text-gray-700 focus:bg-gray-50"
                      data-testid="menu-super-admin"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Super Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="text-red-600 focus:bg-red-50"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Welcome section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm mb-4">
            <Sparkles className="w-4 h-4 text-[#8b5cf6]" />
            <span className="text-sm font-medium text-gray-600">Red de Aprendizaje IA</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-gray-900 mb-2">
            ¡Hola, {displayName}!
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Selecciona un nodo para explorar las rutas de aprendizaje con tu tutor QHUB.BUDDY
          </p>
        </motion.div>

        {/* Reinforcement Assignments */}
        {reinforcements.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 mx-auto max-w-2xl"
            data-testid="reinforcements-section"
          >
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <RotateCcw className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900 text-sm">
                  Refuerzos asignados por tu docente ({reinforcements.length})
                </h3>
              </div>
              <div className="space-y-2">
                {reinforcements.map((r, i) => {
                  const sub = subjectLabels[r.subject] || { name: r.subject, color: "#6b7280" };
                  return (
                    <div
                      key={r.id || i}
                      className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-amber-100 hover:border-amber-300 transition-all"
                      data-testid={`reinforcement-${i}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sub.color }} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            <span className="font-semibold">{r.indicator_code}</span>
                            {r.indicator_text && <span className="text-gray-500"> — {r.indicator_text}</span>}
                          </p>
                          <p className="text-xs text-gray-500">{sub.name} · {r.unit}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="shrink-0 ml-3 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-full px-4"
                        onClick={() => navigate(`/tutor/${r.subject}`, { state: { unitCode: r.unit, unitName: r.unit } })}
                        data-testid={`go-reinforcement-${i}`}
                      >
                        Ir al tutor <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Network Graph */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="relative"
        >
          <NetworkGraph 
            onNodeOpen={handleNodeOpen}
            onNodeTutor={handleNodeTutor}
            hoveredNode={hoveredNode}
            setHoveredNode={setHoveredNode}
            testIdPrefix="dashboard-network"
            enabledSubjects={enabledSubjects}
            enabledProducts={enabledProducts}
          />
        </motion.div>

        {/* Mobile cards fallback */}
        <div className="md:hidden mt-8 grid grid-cols-2 gap-4">
          {[
            { id: "comunicacion-lenguaje", name: "Comunicación", color: "#ef4444" },
            { id: "ciencia-tecnologia", name: "Ciencia", color: "#14b8a6" },
            { id: "matematica-datos", name: "Matemática", color: "#8b5cf6" },
            { id: "ciudadania-valores", name: "Ciudadanía", color: "#f59e0b" },
            { id: "reto-avanzo", name: "Reto Avanzo", color: "#0ea5e9", isReto: true },
            { id: "buddy-avanza", name: "Buddy Avanza", color: "#f97316", isProduct: true },
          ]
            .filter((node) => {
              if (node.isProduct) {
                return !!(enabledProducts && enabledProducts[node.id.replace("-", "_")] === true);
              }
              if (node.isReto) {
                return !!(enabledSubjects && enabledSubjects.reto_avanzo === true);
              }
              if (!enabledSubjects) return true;
              return enabledSubjects[node.id] !== false;
            })
            .map((node) => (
            <button
              key={node.id}
              onClick={() => handleNodeOpen(node.id)}
              className="p-4 rounded-2xl bg-white border-2 text-left transition-all hover:shadow-lg active:scale-95"
              style={{ borderColor: node.color }}
            >
              <div 
                className="w-10 h-10 rounded-xl mb-3 flex items-center justify-center"
                style={{ backgroundColor: `${node.color}15` }}
              >
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: node.color }} />
              </div>
              <p className="font-semibold text-gray-900">{node.name}</p>
              <p className="text-xs text-gray-500">Explorar →</p>
            </button>
          ))}
        </div>

        {/* Quick stats */}
        <motion.div 
          className="mt-8 flex justify-center gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Flame className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.streak}</p>
              <p className="text-xs text-gray-500">Días de racha</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{stats.points}</p>
              <p className="text-xs text-gray-500">Puntos totales</p>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <QHubFooter />

      {/* M3.1 · First-login password gate (intercepts 403 password_change_required) */}
      <FirstLoginPasswordGate
        open={firstLoginGateOpen}
        onClose={() => setFirstLoginGateOpen(false)}
        onSuccess={async () => {
          setFirstLoginGateOpen(false);
          // Retry the access probe now that must_change_password=false.
          try {
            const r = await axios.get(`${API}/avanzo-b2c/access`, { withCredentials: true });
            if (r?.data?.allowed) {
              navigate("/avanzo");
            } else {
              toast.error("Contraseña actualizada, pero Buddy Avanza aún no está disponible.");
            }
          } catch (err) {
            const d = err?.response?.data?.detail;
            if (typeof d === "string" && d.startsWith("consent_")) {
              toast.error(
                d === "consent_pending"
                  ? "Falta registrar tu consentimiento. Contacta a tu institución."
                  : "Tu acceso a Buddy Avanza fue revocado. Contacta a tu institución."
              );
            } else {
              toast.error("No se pudo continuar. Intenta de nuevo.");
            }
          }
        }}
      />
    </div>
  );
};

export default Dashboard;
